import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { Alert, AlertDescription } from './ui/alert';
import { CountryCombobox } from './ui/country-combobox';
import OptiBuildLogo from './OptibuildLogo';
import { ArrowLeft, Building2, Users, MapPin, Phone, Mail, CheckCircle, AlertCircle, MailCheck } from 'lucide-react';
import { userService, RegisterUserData } from '../src/services/user.service';
import { getCountryByCode, countryFlag } from '../src/utils/countries';
import { CURRENCIES } from '../src/utils/currency';

const FORM_CACHE_KEY = 'optibuild_register_draft';

interface RegisterPageProps {
  onBackToLanding: () => void;
  onLogin: () => void;
  onRegistrationComplete: () => void;
  onActivate?: () => void;
}

export function RegisterPage({ onBackToLanding, onLogin, onRegistrationComplete, onActivate }: RegisterPageProps) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState(() => {
    try {
      const cached = sessionStorage.getItem(FORM_CACHE_KEY);
      if (cached) return JSON.parse(cached);
    } catch {}
    return {
      companyName: '',
      companyType: '',
      industry: '',
      companySize: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      country: '',
      currency: '',
      timezone: '',
      website: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      jobTitle: '',
      agreeToTerms: false,
      agreeToMarketing: false,
    };
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(FORM_CACHE_KEY, JSON.stringify(formData));
    } catch {}
  }, [formData]);

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData((prev: typeof formData) => ({ ...prev, [field]: value }));
  };

  const [countryError, setCountryError] = useState(false);

  const handleCountryChange = (code: string) => {
    const country = getCountryByCode(code);
    setCountryError(false);
    setFormData((prev: typeof formData) => ({
      ...prev,
      country: code,
      currency: country?.currency ?? '',
      timezone: country?.timezone ?? '',
    }));
  };

  const handleNextStep = () => {
    if (step === 1 && !formData.country) {
      setCountryError(true);
      return;
    }
    setStep((step + 1) as 1 | 2 | 3 | 4);
  };

  const handlePrevStep = () => {
    setStep((step - 1) as 1 | 2 | 3 | 4);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    setError('');
    
    try {
      // Prepare registration data
      const registrationData: RegisterUserData = {
        // Company Information
        companyName: formData.companyName,
        companyType: formData.companyType,
        industry: formData.industry,
        companySize: formData.companySize,
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        country: formData.country,
        currency: formData.currency,
        timezone: formData.timezone,
        website: formData.website,

        // Personal Information
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: formData.phone,
        jobTitle: formData.jobTitle,
        
        // Account Setup
        agreeToTerms: formData.agreeToTerms,
        agreeToMarketing: formData.agreeToMarketing
      };

      await userService.registerUser(registrationData);
      sessionStorage.removeItem(FORM_CACHE_KEY);
      setStep(4);
    } catch (err: any) {
      console.error('Registration failed:', err);
      setError(err.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep1 = () => (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold">Company Information</h2>
        <p className="text-muted-foreground">Tell us about your construction business</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="companyName">Company Name *</Label>
          <Input
            id="companyName"
            placeholder="ABC Construction Ltd."
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="companyType">Company Type</Label>
          <Select value={formData.companyType} onValueChange={(value) => handleInputChange('companyType', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select company type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="general-contractor">General Contractor</SelectItem>
              <SelectItem value="subcontractor">Subcontractor</SelectItem>
              <SelectItem value="developer">Developer</SelectItem>
              <SelectItem value="architect">Architecture Firm</SelectItem>
              <SelectItem value="engineering">Engineering Firm</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="industry">Primary Industry</Label>
          <Select value={formData.industry} onValueChange={(value) => handleInputChange('industry', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select industry" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="residential">Residential Construction</SelectItem>
              <SelectItem value="commercial">Commercial Construction</SelectItem>
              <SelectItem value="industrial">Industrial Construction</SelectItem>
              <SelectItem value="infrastructure">Infrastructure</SelectItem>
              <SelectItem value="civil">Civil Engineering</SelectItem>
              <SelectItem value="renovation">Renovation & Remodeling</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="companySize">Company Size</Label>
          <Select value={formData.companySize} onValueChange={(value) => handleInputChange('companySize', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select company size" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1-10">1-10 employees</SelectItem>
              <SelectItem value="11-50">11-50 employees</SelectItem>
              <SelectItem value="51-200">51-200 employees</SelectItem>
              <SelectItem value="201-500">201-500 employees</SelectItem>
              <SelectItem value="500+">500+ employees</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-4">
        {/* Country — mandatory, drives currency + timezone */}
        <div className="space-y-2">
          <Label htmlFor="country">
            Country <span className="text-destructive">*</span>
          </Label>
          <CountryCombobox
            value={formData.country}
            onChange={handleCountryChange}
            error={countryError}
          />
          {countryError && (
            <p className="text-xs text-destructive">Please select your country to continue.</p>
          )}
          {/* Derived currency + timezone badges */}
          {formData.country && (
            <div className="flex flex-wrap gap-2 pt-1">
              {(() => {
                const cd = getCountryByCode(formData.country);
                const currencyMeta = CURRENCIES.find(c => c.code === formData.currency);
                return (
                  <>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-xs font-medium">
                      <span className="text-muted-foreground">Currency</span>
                      <span className="font-semibold">
                        {currencyMeta ? `${currencyMeta.symbol} ${currencyMeta.code}` : formData.currency}
                      </span>
                    </span>
                    <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-xs font-medium">
                      <span className="text-muted-foreground">Timezone</span>
                      <span className="font-semibold">{formData.timezone}</span>
                    </span>
                    {cd && (
                      <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted/50 px-2.5 py-0.5 text-xs font-medium">
                        <span className="text-muted-foreground">Code</span>
                        <span className="font-semibold">{countryFlag(cd.code)} {cd.code}</span>
                      </span>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="address">Business Address</Label>
          <Input
            id="address"
            placeholder="123 Construction Ave"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2 space-y-2">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              placeholder="City"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="state">State/Province</Label>
            <Input
              id="state"
              placeholder="State"
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="zipCode">ZIP/Postal Code</Label>
            <Input
              id="zipCode"
              placeholder="12345"
              value={formData.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="website">Company Website (Optional)</Label>
          <Input
            id="website"
            placeholder="https://www.yourcompany.com"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
          />
        </div>
      </div>

      <Button onClick={handleNextStep} className="w-full" disabled={!formData.companyName || isLoading}>
        Next: Contact Information
      </Button>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold">Primary Contact</h2>
        <p className="text-muted-foreground">Your account administrator details</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="firstName">First Name *</Label>
          <Input
            id="firstName"
            placeholder="John"
            value={formData.firstName}
            onChange={(e) => handleInputChange('firstName', e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="lastName">Last Name *</Label>
          <Input
            id="lastName"
            placeholder="Smith"
            value={formData.lastName}
            onChange={(e) => handleInputChange('lastName', e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Business Email *</Label>
          <Input
            id="email"
            type="email"
            placeholder="john@company.com"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <Input
            id="phone"
            placeholder="+1 (555) 123-4567"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
          />
        </div>

        <div className="md:col-span-2 space-y-2">
          <Label htmlFor="jobTitle">Job Title</Label>
          <Input
            id="jobTitle"
            placeholder="Project Manager, CEO, etc."
            value={formData.jobTitle}
            onChange={(e) => handleInputChange('jobTitle', e.target.value)}
          />
        </div>
      </div>

      <div className="flex gap-4">
        <Button variant="outline" onClick={handlePrevStep} className="flex-1" disabled={isLoading}>
          Previous
        </Button>
        <Button 
          onClick={handleNextStep} 
          className="flex-1"
          disabled={!formData.firstName || !formData.lastName || !formData.email || isLoading}
        >
          Next: Account Setup
        </Button>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold">Account Setup</h2>
        <p className="text-muted-foreground">Review and complete your registration</p>
      </div>

      <div className="bg-muted/50 p-4 rounded-lg">
        <h3 className="font-medium mb-2">Registration Summary</h3>
        <div className="text-sm space-y-1 text-muted-foreground">
          <p><strong>Company:</strong> {formData.companyName}</p>
          <p><strong>Contact:</strong> {formData.firstName} {formData.lastName}</p>
          <p><strong>Email:</strong> {formData.email}</p>
          {formData.phone && <p><strong>Phone:</strong> {formData.phone}</p>}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start space-x-2">
          <Checkbox
            id="agreeToTerms"
            checked={formData.agreeToTerms}
            onCheckedChange={(checked) => handleInputChange('agreeToTerms', checked as boolean)}
          />
          <Label htmlFor="agreeToTerms" className="text-sm leading-relaxed">
            I agree to OptiBuild's{' '}
            <button
              type="button"
              onClick={() => window.open('/terms', '_blank', 'noopener,noreferrer')}
              className="text-primary hover:underline font-normal"
            >
              Terms of Service
            </button>
            {' '}and{' '}
            <button
              type="button"
              onClick={() => window.open('/privacy', '_blank', 'noopener,noreferrer')}
              className="text-primary hover:underline font-normal"
            >
              Privacy Policy
            </button>
          </Label>
        </div>

        <div className="flex items-start space-x-2">
          <Checkbox
            id="agreeToMarketing"
            checked={formData.agreeToMarketing}
            onCheckedChange={(checked) => handleInputChange('agreeToMarketing', checked as boolean)}
          />
          <Label htmlFor="agreeToMarketing" className="text-sm leading-relaxed">
            I'd like to receive updates about OptiBuild features and construction industry insights
          </Label>
        </div>
      </div>

      <div className="flex gap-4">
        <Button variant="outline" onClick={handlePrevStep} className="flex-1" disabled={isLoading}>
          Previous
        </Button>
        <Button 
          onClick={handleSubmit} 
          className="flex-1"
          disabled={!formData.agreeToTerms || isLoading}
        >
          {isLoading ? 'Creating Account...' : 'Create OptiBuild Account'}
        </Button>
      </div>
      
      {/* Validation feedback */}
      {!formData.agreeToTerms && (
        <div className="mt-4 p-3 bg-muted rounded-lg text-sm">
          <p className="font-medium mb-2">To create account, please:</p>
          <ul className="space-y-1">
            {!formData.agreeToTerms && <li className="text-red-500">• Agree to Terms of Service</li>}
          </ul>
        </div>
      )}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <MailCheck className="h-8 w-8 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-green-900">Account Created!</h2>
        <p className="text-muted-foreground mt-1">
          Check your inbox to activate your account
        </p>
      </div>

      <div className="bg-muted/50 border rounded-lg p-4 space-y-3">
        <p className="text-sm font-medium">An activation email has been sent to:</p>
        <p className="text-sm font-mono bg-background border rounded px-3 py-2 break-all">
          {formData.email}
        </p>
        <p className="text-sm text-muted-foreground">
          Click the link in that email to verify your address and activate your account before signing in.
          If you don't see it, check your spam folder.
        </p>
      </div>

      <div className="space-y-3">
        <Button onClick={() => onActivate ? onActivate() : onLogin()} className="w-full">
          Activate Account
        </Button>
        <Button variant="outline" onClick={onLogin} className="w-full">
          Go to Sign In
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <Button variant="ghost" onClick={onBackToLanding} className="mb-6">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Button>
          
          <OptiBuildLogo size="lg" variant="full" className="mx-auto mb-6" />
          
          {step < 4 && (
            <div className="flex items-center justify-center space-x-4 mb-8">
              {[1, 2, 3].map((stepNum) => (
                <div key={stepNum} className="flex items-center">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${step >= stepNum ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}
                  `}>
                    {step > stepNum ? <CheckCircle className="h-4 w-4" /> : stepNum}
                  </div>
                  {stepNum < 3 && (
                    <div className={`w-12 h-0.5 mx-2 ${step > stepNum ? 'bg-primary' : 'bg-muted'}`} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Form Card */}
        <Card>
          <CardContent className="p-8">
            {step === 1 && renderStep1()}
            {step === 2 && renderStep2()}
            {step === 3 && renderStep3()}
            {step === 4 && renderStep4()}
          </CardContent>
        </Card>

        {step < 4 && (
          <div className="text-center mt-6">
            <p className="text-sm text-muted-foreground">
              Already have an OptiBuild account?{' '}
              <button
                onClick={onLogin}
                className="text-primary hover:underline font-medium"
              >
                Sign in here
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}